import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useLayoutEffect,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const DashboardContext = createContext();

/** `activeView` values that map to `/dashboard#...` (fragment only; no new routes). */
const DASHBOARD_HASH_MODULES = new Set([
    'dashboard',
    'feed',
    'teams',
    'projects',
    'notes',
    'filesync',
    'minisite',
    'calendar',
    'meeting',
    'email',
    'talk',
    'noticeboard',
]);

function parseHashToModule(hashSource) {
    const raw = String(hashSource || '')
        .replace(/^#/, '')
        .trim();
    if (!raw) return 'dashboard';
    const key = raw.toLowerCase();
    return DASHBOARD_HASH_MODULES.has(key) ? key : 'dashboard';
}

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboard must be used within a DashboardProvider');
    }
    return context;
};

export const DashboardProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [activeView, setActiveViewState] = useState(() =>
        parseHashToModule(typeof window !== 'undefined' ? window.location.hash : '')
    );
    const [returnView, setReturnViewState] = useState('dashboard');
    const [deepLink, setDeepLink] = useState(null); // { target_type, target_id, target_meta }
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    // Mobile sidebar drawer (Responsive-Mobile-Plan.md Phase 0/1): separate from
    // isSidebarCollapsed, which is the desktop icon-only collapse. Below
    // --bp-tablet-lg (1024px) the sidebar renders as a floating overlay drawer
    // that is closed by default and toggled by a hamburger button in Header;
    // above that breakpoint it stays permanently docked and this flag is unused.
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const toggleMobileSidebar = useCallback(() => setIsMobileSidebarOpen((v) => !v), []);
    const closeMobileSidebar = useCallback(() => setIsMobileSidebarOpen(false), []);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [highlightedTeamId, setHighlightedTeamId] = useState(null);
    const [stats, setStats] = useState({
        mrrToday: 903,
        mrrYesterday: 1103,
        mrrThisMonth: 8903,
        totalMrr: '8903k',
        payingCustomers: 8903,
        freePlanCustomers: '89.5k',
        thisMonthSignup: 5000,
        dailyActiveUsers: 1034
    });

    const applyHashForView = useCallback(
        (view) => {
            if (!DASHBOARD_HASH_MODULES.has(view)) return;

            const nextHash = view === 'dashboard' ? '' : view;
            const current = (location.hash || '').replace(/^#/, '');
            if (current === nextHash) return;

            navigate(
                {
                    pathname: location.pathname,
                    search: location.search,
                    hash: nextHash,
                },
                { replace: true }
            );
        },
        [navigate, location.pathname, location.search, location.hash]
    );

    const setActiveView = useCallback(
        (view, options = {}) => {
            setReturnViewState(options.returnTo || 'dashboard');
            setActiveViewState(view);
            applyHashForView(view);
            setIsMobileSidebarOpen(false);
        },
        [applyHashForView]
    );

    useLayoutEffect(() => {
        const raw = (location.hash || '').replace(/^#/, '').trim();
        const key = raw.toLowerCase();
        if (raw && !DASHBOARD_HASH_MODULES.has(key)) {
            navigate(
                {
                    pathname: location.pathname,
                    search: location.search,
                    hash: '',
                },
                { replace: true }
            );
        }
    }, [navigate, location.pathname, location.search, location.hash]);

    useEffect(() => {
        const onHashChange = () => {
            const raw = window.location.hash.replace(/^#/, '').trim();
            const key = raw.toLowerCase();
            const next = parseHashToModule(window.location.hash);
            setActiveViewState(next);
            if (raw && !DASHBOARD_HASH_MODULES.has(key)) {
                navigate(
                    {
                        pathname: location.pathname,
                        search: location.search,
                        hash: '',
                    },
                    { replace: true }
                );
            }
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, [navigate, location.pathname, location.search]);

    const value = {
        activeView,
        setActiveView,
        returnView,
        deepLink,
        setDeepLink,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        isMobileSidebarOpen,
        toggleMobileSidebar,
        closeMobileSidebar,
        isNotificationsOpen,
        setIsNotificationsOpen,
        highlightedTeamId,
        setHighlightedTeamId,
        stats,
        setStats
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};
